import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, useIntl } from 'react-intl';
import Heading from '@bibliocommons/heading';
import { ChevronLink } from '@bibliocommons/base-links';
import { GreenBadge, DarkRedBadge, DarkGreyBadge } from '@bibliocommons/base-badges';
import { EVENTS_URL } from 'app/constants/EventsConstants';
import { eventShape } from 'app/shapes/events';
import ContentCard from 'app/components/cards/ContentCard';
import { getRegistrationStatusMessage } from 'app/helpers/events/utils';
import mapEventToEventCard from 'app/helpers/events/mapEventToEventCard';

import './SeriesEvents.scss';

const WAITLIST = 'waitlist';

export default function SeriesEvents({
  seriesEvents,
  location,
  seriesId,
  isSeriesRegistration,
  trackSeriesEventLinkClick,
  trackViewAllUpcomingEventsClick
}) {
  const intl = useIntl();

  if (seriesEvents?.size === 0) return null;

  let cardId = 1;
  const events = seriesEvents.slice(0, 3).valueSeq();
  const href = `${EVENTS_URL}?series=${seriesId}`;

  const viewAllMessage = {
    id: 'events.view.upcoming',
    defaultMessage: `View all upcoming events (${seriesEvents?.size})`,
    values: { count: seriesEvents?.size }
  };

  const viewAllLinkText = intl.formatMessage(viewAllMessage, viewAllMessage.values);

  function renderHeading() {
    if (isSeriesRegistration) {
      return (
        <Heading className="event-section-heading" tagName="h2" size="moderate" variant>
          <FormattedMessage id="events.related.series_events" defaultMessage="Events in this series" />
        </Heading>
      );
    }
    return (
      <Heading className="event-section-heading" tagName="h2" size="moderate" variant>
        <FormattedMessage id="events.related.series" defaultMessage="This event is also offered on" />
      </Heading>
    );
  }

  function renderBadge(event, registrationStatus) {
    const isCancelled = event.getIn(['definition', 'isCancelled']);
    if (isCancelled) {
      return (
        <span className="event-status-badge">
          <DarkRedBadge size="small">
            <FormattedMessage id="cancelled" />
          </DarkRedBadge>
        </span>
      );
    }

    if (registrationStatus?.state === WAITLIST) {
      return (
        <span className="event-status-badge">
          <GreenBadge size="small">
            <FormattedMessage id="join_waitlist" />
          </GreenBadge>
        </span>
      );
    }

    if (event?.get('isFull')) {
      return (
        <span className="event-status-badge">
          <DarkGreyBadge size="small">
            <FormattedMessage id="registration_full" />
          </DarkGreyBadge>
        </span>
      );
    }

    return null;
  }

  function renderEventCard(event, eventIndex) {
    const registrationStatus = getRegistrationStatusMessage(event);
    const card = mapEventToEventCard(cardId, event, location);
    cardId++;

    return (
      <li className="events-list" key={`event-card_${eventIndex}`}>
        <ContentCard
          className="event-card"
          card={card}
          trackSeriesEventLinkClick={linkText => trackSeriesEventLinkClick({ linkText, eventData: event })}
          hasBackground={false}
          hasBorder={false}
          hasDescription={false}
          hasType={false}
          dateStampSize="default"
          renderBadge={() => renderBadge(event, registrationStatus)}
          dateLink={card.get('url')}
        />
      </li>
    );
  }

  return (
    <div className="cp-series-events">
      {renderHeading()}
      <div className="event-series">{events.map(renderEventCard)}</div>
      <ChevronLink
        dataKey="all-upcoming-dates"
        className="all-dates"
        href={href}
        handleClick={() => trackViewAllUpcomingEventsClick({ linkText: viewAllLinkText })}
      >
        <span aria-hidden>{viewAllLinkText}</span>
      </ChevronLink>
    </div>
  );
}

SeriesEvents.propTypes = {
  seriesEvents: ImmutablePropTypes.mapOf(eventShape, PropTypes.string).isRequired,
  location: ImmutablePropTypes.map,
  seriesId: PropTypes.string.isRequired,
  isSeriesRegistration: PropTypes.bool.isRequired,
  trackSeriesEventLinkClick: PropTypes.func.isRequired,
  trackViewAllUpcomingEventsClick: PropTypes.func.isRequired
};
